<template>
  <div>
    <b-modal
      v-if="blocking"
      :key="'wfPromptBlocking-'+blocking.prompt.stateID"
      :title="pVal(blocking.prompt, 'title', 'Workflow prompt')"
      visible
      centered
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      :no-enforce-focus="false"
    >
      <component
        :is="blocking.component"
        :payload="blocking.prompt.payload"
        :loading="isLoading"
        @submit="resumeToast({ input: $event, prompt: blocking.prompt })"
      />
    </b-modal>

    <b-toast
      v-for="({ prompt, component, passive }) in toasts"
      :id="'wfPromptToast-'+prompt.stateID"
      :key="'wfPromptToast-'+prompt.stateID"
      :variant="pVal(prompt, 'variant', 'primary')"
      :visible="!isActive"
      solid
      :no-auto-hide="!passive"
      :auto-hide-delay="pVal(prompt, 'timeout', defaultTimeout) * 1000"
      @hide="onToastHide({ prompt, passive })"
    >
      <template #toast-title>
        <strong>{{ pVal(prompt, 'title', 'Workflow prompt') }}</strong>
      </template>

      <component
        :is="component"
        v-if="component"
        :payload="prompt.payload"
        :loading="isLoading"
        @submit="resumeToast({ input: $event, prompt })"
      />
    </b-toast>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import definitions from './kinds/index.ts'
import { pVal } from './utils.ts'

function isBlocking ({ payload }) {
  const v = pVal(payload, 'blocking', false)
  return v === true || v === 'true'
}

export default {
  name: 'CPromptToast',

  props: {
    hideToasts: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      passivePrompts: [],

      hasFocus: null,
      hasFocusObserver: 0,
    }
  },

  computed: {
    ...mapGetters({
      prompts: 'wfPrompts/all',
      isActive: 'wfPrompts/isActive',
      isLoading: 'wfPrompts/isLoading',
    }),

    /**
     * Prompts with handlers, observed with "watch"
     *
     * Prompts are only returned when document has focus!
     *
     * @returns {*}
     */
    withHandlers () {
      return (this.hasFocus ? this.prompts : [])
        .filter(({ ref }) => !!definitions[ref] && !!definitions[ref].handler)
        .map(prompt => ({ ...definitions[prompt.ref], prompt }))
    },

    /**
     * Prompts with components
     *
     * Prompts are only returned when document has focus!
     *
     * @returns {*}
     */
    withComponents () {
      return (this.hasFocus ? this.prompts : [])
        .filter(({ ref }) => !!definitions[ref] && !!definitions[ref].component)
        .map(prompt => ({ ...definitions[prompt.ref], prompt }))
    },

    /**
     * All non-passive prompts with components
     */
    activePrompts () {
      return this.withComponents.filter(({ passive, prompt }) => !passive && !isBlocking(prompt))
    },

    /**
     * Oldest blocking prompt; it is shown as a modal the user can only leave by answering
     */
    blocking () {
      return this.withComponents.find(({ passive, prompt }) => !passive && isBlocking(prompt))
    },

    /**
     * Returns list of prompts that we can interpret as toasts: display component is defined
     *
     * Toasts (prompts with components) are displayed in order received but
     * passive (no feedback or input from user required) first and the rest later
     */
    toasts () {
      return this.hideToasts
        ? []
        : [
          ...this.passivePrompts,
          ...this.activePrompts,
        ]
    },

    defaultTimeout () {
      return 7
    },
  },

  watch: {
    // watch prompts with handlers and when a new one arrives
    // shift it from the stack, resume the prompt and handle it
    withHandlers (hh) {
      if (hh.length > 0) {
        const { handler, prompt } = hh.shift()
        this.resume({ input: {}, prompt }).then(() => {
          handler.call(this, prompt.payload)
        })
      }
    },

    /**
     * Make a copy of prompt if it is defined as passive
     *
     * We do this because we do not want it to be removed right away
     * but through a toast component's timeout
     */
    withComponents: {
      immediate: true,
      handler (wc) {
        wc.forEach(p => {
          if (p.passive && !this.passivePrompts.some(({ prompt }) => prompt.stateID === p.prompt.stateID)) {
            this.passivePrompts.push(p)
          }
        })
      },
    },
  },

  mounted () {
    this.setDocumentFocusObserver()
  },

  beforeUnmount () {
    this.clearDocumentFocusObserver()
    this.setDefaultValues()
  },

  methods: {
    ...mapActions({
      resume: 'wfPrompts/resume',
      cancel: 'wfPrompts/cancel',
      activate: 'wfPrompts/activate',
    }),

    resumeToast (values) {
      // Only reset input if prompt is kept open
      if (values.input && values.input.keep) {
        values.input = {}
      }

      this.resume(values)
    },

    onToastHide ({ prompt, passive }) {
      setTimeout(() => {
        if (passive) {
          this.passivePrompts = this.passivePrompts.filter(({ prompt: p }) => p.stateID !== prompt.stateID)
        } else {
          this.cancel(prompt)
        }
      }, 300)
    },

    pVal (prompt, k, def = undefined) {
      return pVal(prompt.payload, k, def)
    },

    clearDocumentFocusObserver () {
      if (this.hasFocusObserver) {
        window.clearInterval(this.hasFocusObserver)
      }
    },

    /**
     * Interval check if window has focus
     */
    setDocumentFocusObserver () {
      this.clearDocumentFocusObserver()

      this.hasFocusObserver = window.setInterval(() => {
        const f = document.hasFocus()
        if (this.hasFocus !== f) {
          this.hasFocus = f
        }
      }, 1000)
    },

    setDefaultValues () {
      this.toasts = []
      this.hasFocus = null
      this.hasFocusObserver = 0
    },
  },
}
</script>

<style lang="scss">
.toast-header {
  align-items: start;
  padding: 0.375rem 0.75rem;

  strong {
    word-break: break-word;
  }

  .close {
    margin-bottom: 0 !important;
  }
}

// .b-toaster-leave-active {
//   width: 100%;
// }

.b-toaster {
  &.b-toaster-top-right,
  &.b-toaster-top-left,
  &.b-toaster-bottom-right,
  &.b-toaster-bottom-left {
    .b-toast {
      &.b-toaster-enter-active,
      &.b-toaster-leave-active,
      &.b-toaster-move {
        transition: transform 0.3s ease-in-out; /* Adjust the timing function for smoother transitions */
        opacity: 1; /* Ensure opacity is set to avoid flickering during transition */
      }

      &.b-toaster-enter {
        transform: translate(0, -100%); /* Start off-screen when entering */
        opacity: 0; /* Start with 0 opacity */
      }

      &.b-toaster-enter-to,
      &.b-toaster-enter-active {
        transform: translate(0, 0); /* Move to the visible position */
        opacity: 1; /* Fade in during the transition */
      }

      &.b-toaster-leave-active {
        position: absolute;
        transform: translate(0, -100%); /* Move off-screen when leaving */
        opacity: 0; /* Fade out during the transition */
      }

      &.b-toaster-leave-to {
        opacity: 0; /* Ensure 0 opacity at the end of the transition */
      }
    }
  }
}
</style>
